import { getApp, getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { Platform } from "react-native";
import { canUseFirebaseEmulators } from "./firebase-emulator";

const firebaseConfig = {
  apiKey: "AIzaSyDVaN-rh_oMI0_LrjFLxAvKLiBz56WesfY",
  authDomain: "wehonorx.firebaseapp.com",
  projectId: "wehonorx",
  storageBucket: "wehonorx.firebasestorage.app",
  messagingSenderId: "486321965097",
  appId: "1:486321965097:web:71cf8c848589381bbdb438",
  measurementId: "G-KMCG93JPM0",
};

export const firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

connectConfiguredFirebaseEmulators();

function connectConfiguredFirebaseEmulators() {
  if (Platform.OS !== "web") return;

  const emulatorModeEnabled = process.env.EXPO_PUBLIC_FIREBASE_EMULATORS_ENABLED === "true";
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  if (!canUseFirebaseEmulators(hostname, emulatorModeEnabled)) return;

  const authHost = process.env.EXPO_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST;
  const functionsHost = process.env.EXPO_PUBLIC_FIREBASE_FUNCTIONS_EMULATOR_HOST;
  if (!authHost && !functionsHost) return;

  const state = globalThis as typeof globalThis & { __wehonorFirebaseEmulatorsConnected?: boolean };
  if (state.__wehonorFirebaseEmulatorsConnected) return;

  if (authHost) {
    connectAuthEmulator(getAuth(firebaseApp), `http://${authHost}`, { disableWarnings: true });
  }
  if (functionsHost) {
    const [host, port] = parseEmulatorHost(functionsHost);
    connectFunctionsEmulator(getFunctions(firebaseApp, "us-east1"), host, port);
  }
  state.__wehonorFirebaseEmulatorsConnected = true;
}

function parseEmulatorHost(value: string): [string, number] {
  const separator = value.lastIndexOf(":");
  const host = separator > 0 ? value.slice(0, separator) : value;
  const port = Number(separator > 0 ? value.slice(separator + 1) : "5001");
  if (!host || !Number.isInteger(port) || port < 1 || port > 65_535) {
    throw new Error("Invalid Firebase Functions emulator host configuration.");
  }
  return [host, port];
}

export async function bootFirebaseAnalytics() {
  if (Platform.OS !== "web" || process.env.EXPO_PUBLIC_DISABLE_ANALYTICS === "true") {
    return null;
  }

  const { getAnalytics, isSupported } = await import("firebase/analytics");

  if (!(await isSupported())) {
    return null;
  }

  return getAnalytics(firebaseApp);
}

export async function recordCentralClientError(context: string, errorName = "Error") {
  if (Platform.OS !== "web") return;
  try {
    const analytics = await bootFirebaseAnalytics();
    if (!analytics) return;
    const { logEvent } = await import("firebase/analytics");
    logEvent(analytics, "exception", { description: `${context}:${errorName}`.slice(0, 120), fatal: true });
  } catch {
    // Error reporting must never create a second failure or block recovery.
  }
}

export async function recordCentralClientMetric(metricName: string, valueMs: number, routeFamily: string) {
  if (Platform.OS !== "web" || !Number.isFinite(valueMs) || valueMs < 0) return;
  try {
    const analytics = await bootFirebaseAnalytics();
    if (!analytics) return;
    const { logEvent } = await import("firebase/analytics");
    logEvent(analytics, "central_client_metric", {
      metric_name: metricName.replace(/[^a-z0-9_]/gi, "_").slice(0, 40),
      route_family: routeFamily.slice(0, 80),
      value_ms: Math.round(valueMs),
    });
  } catch {
    // Telemetry is best-effort and must never affect the staff workflow.
  }
}
